import React, { useEffect, useMemo, useRef, useState } from "react";
import { MpesaMenu } from "@/components/Holizontal/MpesaMenu";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api";
import { helper } from "@/lib/helper";
import { ArrowRight, ArrowLeft, Upload, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { RowActionMenu } from "@/components/RowActionMenu";
import { hasPerm } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { ReconcileDialog } from "./dialog/ReconcileDialog";
import { UploadDialog } from "./dialog/UploadDialog";
import { toastError, toastSuccess } from "@/lib/toast";

type MpesaRow = {
  id: number;
  transaction_code?: string;
  transaction_phone?: string;
  transaction_names?: string;
  transaction_amount?: number | string;
  transaction_date?: string; // from DB
  created_at?: string;
  USERNAME?: string; // joined user.username
};

type ApiResponse = {
  data: MpesaRow[];
  meta: {
    per_page: number;
    next_cursor: string | null;
    prev_cursor: string | null;
    has_more: boolean;
  };
  anchor_id: number | null;
};

const PER_PAGE = 10;

function parseAmount(a: MpesaRow["transaction_amount"]) {
  if (a == null) return 0;
  if (typeof a === "number") return a;
  const n = Number(String(a).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function truncateHashLocal(value?: string, visible = 5) {
  if (!value) return "-";
  const s = String(value);
  if (s.length <= visible) return s;
  return `${s.slice(0, visible)}...`;
}

function useDebounced<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export function MpesaUnResolvedCollectionPage() {
  const [bootLoading, setBootLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [rows, setRows] = useState<MpesaRow[]>([]);
  const [anchorId, setAnchorId] = useState<number | null>(null);

  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [prevCursor, setPrevCursor] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const qDebounced = useDebounced(q, 350);

  const listTopRef = useRef<HTMLDivElement | null>(null);
  const [pendingScrollTop, setPendingScrollTop] = useState(false);

  useEffect(() => {
    if (!pendingScrollTop) return;
    if (loading) return;

    requestAnimationFrame(() => {
      listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    setPendingScrollTop(false);
  }, [pendingScrollTop, loading, rows.length]);

  // reset snapshot when search changes
  useEffect(() => {
    setCursor(null);
    setAnchorId(null);
  }, [qDebounced]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("per_page", String(PER_PAGE));

    if (qDebounced.trim()) params.set("q", qDebounced.trim());
    if (cursor) params.set("cursor", cursor);
    if (anchorId != null) params.set("anchor_id", String(anchorId));

    return params.toString();
  }, [qDebounced, cursor, anchorId]);

  async function load(isBoot = false) {
    if (isBoot) setBootLoading(true);
    setLoading(true);
    setError(null);

    try {
      const json = await apiFetch<ApiResponse>(
        `/mpesa/unresolved-collections?${queryString}`
      );

      setRows(json.data ?? []);
      setNextCursor(json.meta?.next_cursor ?? null);
      setPrevCursor(json.meta?.prev_cursor ?? null);

      setAnchorId((prev) => (prev == null ? json.anchor_id ?? null : prev));
    } catch (e: any) {
      setError(e?.message ?? "Failed to load");
      setRows([]);
      setNextCursor(null);
      setPrevCursor(null);
    } finally {
      setLoading(false);
      if (isBoot) setBootLoading(false);
    }
  }

  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (bootLoading) return;
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  const totals = useMemo(() => {
    const totalAmount = rows.reduce(
      (s, r) => s + parseAmount(r.transaction_amount),
      0
    );
    return { count: rows.length, totalAmount };
  }, [rows]);

  const resetAll = () => {
    setQ("");
    setCursor(null);
    setAnchorId(null);
  };

  const goNext = () => {
    if (!nextCursor || loading) return;
    setPendingScrollTop(true);
    setCursor(nextCursor);
  };

  const goPrev = () => {
    if (!prevCursor || loading) return;
    setPendingScrollTop(true);
    setCursor(prevCursor);
  };

  const onResolve = (row: MpesaRow) => {
    alert(`Resolve: ${row.transaction_code ?? row.id}`);
  };

  const truncatePhone = (v?: string) =>
    typeof (helper as any).truncateHash === "function"
      ? (helper as any).truncateHash(v)
      : truncateHashLocal(v, 5);

  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const isRTL = dir === "rtl";

  //
  const canResolvePayment = hasPerm("unresolved-mpesa-records");

  const [openReconcile, setOpenReconcile] = useState(false)
  const [openUpload, setOpenUpload] = useState(false)

  const [autoLoading, setAutoLoading] = useState(false);

  const handleAutoReconcile = async () => {
    try {
      setAutoLoading(true);

      await apiFetch("/mpesa/autorepay", {
        method: "POST",
      });

      toastSuccess("Auto reconciliation completed");
    } catch (e: any) {
      toastError(e?.message || "Failed to auto reconcile");
    } finally {
      setAutoLoading(false);
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 py-4 space-y-4">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        {/* Left */}
        <div>
          <h1 className="text-2xl font-bold">Mpesa Unresolved Collections</h1>
          <p className="text-sm text-muted-foreground">
            Unmatched Mpesa rows. Per page fixed at {PER_PAGE}.
          </p>
        </div>

        {/* Right */}
        <div className="flex items-center gap-1">
          <Button
            onClick={handleAutoReconcile}
            disabled={autoLoading}
            className="gap-2"
          >
            {autoLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {autoLoading ? "Processing..." : "Auto Reconcile"}
          </Button>
          <Button onClick={() => setOpenUpload(true)}>
            <Upload className="h-4 w-4" />
            Upload
          </Button>
        </div>
      </div>
      <MpesaMenu />

      {loading && (
        <div className="sticky top-0 z-10">
          <div className="h-1 w-full bg-muted overflow-hidden rounded">
            <motion.div
              className="h-full bg-primary"
              initial={{ x: "-100%" }}
              animate={{ x: "100%" }}
              transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-3 sm:p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Search</div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Transaction code / phone / name..."
              disabled={loading}
              className={cn(
                "h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                loading && "opacity-60 cursor-not-allowed"
              )}
            />
          </div>

          <div className="flex items-end justify-between sm:justify-end gap-2">
            <button
              onClick={resetAll}
              disabled={loading}
              className={cn(
                "h-10 px-3 rounded-md border border-border text-sm font-medium",
                loading ? "opacity-60 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              Reset
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-sm pt-1">
          <div className="px-3 py-1 rounded-full bg-muted">
            Rows: <span className="font-semibold">{totals.count}</span>
          </div>
          <div className="px-3 py-1 rounded-full bg-muted">
            Total:{" "}
            <span className="font-semibold">
              {helper.formatMoneyKES(totals.totalAmount)}
            </span>
          </div>
          <div className="px-3 py-1 rounded-full bg-muted">
            Anchor: <span className="font-semibold">{anchorId ?? "-"}</span>
          </div>
        </div>
      </div>

      {/* Listing */}
      <div className="bg-card border border-border rounded-xl p-3 sm:p-4 relative">
        <div ref={listTopRef} />

        {loading && (
          <div className="absolute inset-0 z-10 rounded-xl bg-background/60 backdrop-blur-[1px] flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm">
              <motion.span
                className="h-4 w-4 rounded-full border-2 border-muted-foreground border-t-transparent"
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 0.8, ease: "linear" }}
              />
              Loading unresolved mpesa rows...
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive mb-3">
            {error}
          </div>
        )}

        {/* Mobile cards */}
        <div className="md:hidden space-y-2">
          {!loading && rows.length === 0 ? (
            <div className="rounded-lg border border-border p-5 text-center text-sm text-muted-foreground">
              No unresolved mpesa rows found.
            </div>
          ) : (
            rows.map((r) => (
              <div
                key={r.id}
                className="rounded-xl border border-border bg-background p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">
                      {r.transaction_code ?? `#${r.id}`}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {truncatePhone(r.transaction_phone)} •{" "}
                      {r.transaction_names ?? "-"}
                    </div>
                  </div>

                  <RowActionMenu
                    isRTL={isRTL}
                  //onConvert={canResolvePayment ? () => openResolveConfirm(r) : undefined}
                  />
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">Amount</div>
                    <div className="font-bold">
                      {helper.formatMoneyKES(parseAmount(r.transaction_amount))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Date</div>
                    <div className="font-medium">
                      {helper.compactDateTime(r.transaction_date ?? r.created_at)}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-xs text-muted-foreground">Created By</div>
                    <div className="font-medium">{r.USERNAME ?? "MPESA"}</div>
                  </div>
                </div>
              </div>
            ))
          )}

          <div className="mt-4 flex items-center justify-between">
            <button
              onClick={goPrev}
              disabled={!prevCursor || loading}
              className={cn(
                "h-10 px-3 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
                !prevCursor || loading
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-muted"
              )}
            >
              <ArrowLeft className="h-4 w-4" />
              Prev
            </button>

            <button
              onClick={goNext}
              disabled={!nextCursor || loading}
              className={cn(
                "h-10 px-3 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
                !nextCursor || loading
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-muted"
              )}
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="min-w-[1000px] w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Txn Code</th>
                  <th className="px-4 py-3 font-semibold">Paid Phone</th>
                  <th className="px-4 py-3 font-semibold">Amount</th>
                  <th className="px-4 py-3 font-semibold">Paid At</th>
                  <th className="px-4 py-3 font-semibold">Mpesa Payer</th>
                  <th className="px-4 py-3 font-semibold">Created By</th>
                  <th className="px-4 py-3 font-semibold text-right">Action</th>
                </tr>
              </thead>

              <tbody>
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                      No unresolved mpesa rows found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-3 font-medium">
                        {r.transaction_code ?? `#${r.id}`}
                      </td>
                      <td className="px-4 py-3">
                        <span title={r.transaction_phone}>
                          {truncatePhone(r.transaction_phone)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {helper.formatMoneyKES(parseAmount(r.transaction_amount))}
                      </td>
                      <td className="px-4 py-3">
                        {helper.compactDateTime(r.transaction_date ?? r.created_at)}
                      </td>
                      <td className="px-4 py-3">{r.transaction_names ?? "-"}</td>
                      <td className="px-4 py-3">{r.USERNAME ?? "MPESA"}</td>
                      <td className="px-4 py-3 text-right">
                        <RowActionMenu
                          isRTL={isRTL}
                        //onConvert={canResolvePayment ? () => openResolveConfirm(r) : undefined}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <button
              onClick={goPrev}
              disabled={!prevCursor || loading}
              className={cn(
                "h-10 px-3 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
                !prevCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              <ArrowLeft className="h-4 w-4" />
              Prev
            </button>

            <button
              onClick={goNext}
              disabled={!nextCursor || loading}
              className={cn(
                "h-10 px-3 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
                !nextCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      <ReconcileDialog open={openReconcile} onOpenChange={setOpenReconcile} />
      <UploadDialog open={openUpload} onOpenChange={setOpenUpload} />
    </div>
  );
}